import React, { createContext, useState, useContext, ReactNode } from "react";

type Language = "pl" | "en" | "ru" | "uk";

interface LanguageContextProps {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const translations = {
  // Navigation
  "nav.home": {
    pl: "Strona Główna",
    en: "Home",
    ru: "Главная",
    uk: "Головна",
  },
  "nav.about": {
    pl: "O Samie",
    en: "About Sam",
    ru: "О Сэме",
    uk: "Про Сема",
  },
  "nav.gallery": {
    pl: "Galeria",
    en: "Gallery",
    ru: "Галерея",
    uk: "Галерея",
  },
  "nav.press": {
    pl: "Dla Prasy",
    en: "Press",
    ru: "Пресса",
    uk: "Преса",
  },
  "nav.events": {
    pl: "Wydarzenia",
    en: "Events",
    ru: "События",
    uk: "Події",
  },
  "nav.final": {
    pl: "Wielki Finał",
    en: "Grand Final",
    ru: "Грандиозный Финал",
    uk: "Великий Фінал",
  },
  "nav.partners": {
    pl: "Partnerzy",
    en: "Partners",
    ru: "Партнеры",
    uk: "Партнери",
  },
  "nav.faq": {
    pl: "FAQ",
    en: "FAQ",
    ru: "FAQ",
    uk: "FAQ",
  },
  "nav.contact": {
    pl: "Kontakt",
    en: "Contact",
    ru: "Контакты",
    uk: "Контакти",
  },
  "nav.buyTicket": {
    pl: "Kup Bilet",
    en: "Buy Ticket",
    ru: "Купить Билет",
    uk: "Купити Квиток",
  },

  // Hero Section
  "hero.title": {
    pl: "Sam Childers",
    en: "Sam Childers",
    ru: "Сэм Чайлдерс",
    uk: "Сем Чайлдерс",
  },
  "hero.subtitle": {
    pl: "Kaznodzieja z Karabinem",
    en: "Machine Gun Preacher",
    ru: "Проповедник с Пулеметом",
    uk: "Проповідник з Кулеметом",
  },
  "hero.description": {
    pl: "Poznaj inspirującą historię człowieka, który poświęcił swoje życie ratowaniu dzieci w strefach wojennych Afryki.",
    en: "Discover the inspiring story of a man who dedicated his life to saving children from war zones in Africa.",
    ru: "Узнайте вдохновляющую историю человека, посвятившего свою жизнь спасению детей из военных зон Африки.",
    uk: "Дізнайтеся надихаючу історію людини, яка присвятила своє життя порятунку дітей із зон військових дій в Африці.",
  },
  "hero.cta": {
    pl: "Dowiedz się więcej",
    en: "Learn More",
    ru: "Узнать больше",
    uk: "Дізнатися більше",
  },

  // About Section
  "about.title": {
    pl: "O Samie",
    en: "About Sam",
    ru: "О Сэме",
    uk: "Про Сема",
  },
  "about.subtitle": {
    pl: "Poznaj historię człowieka, który poświęcił życie ratowaniu dzieci w Afryce",
    en: "Learn the story of a man who dedicated his life to saving children in Africa",
    ru: "Узнайте историю человека, посвятившего свою жизнь спасению детей в Африке",
    uk: "Дізнайтеся історію людини, яка присвятила своє життя порятунку дітей в Африці",
  },
  "about.description": {
    pl: "Sam Childers, znany jako 'Kaznodzieja z Karabinem', to były członek gangu motocyklowego, który przeszedł niezwykłą transformację. Po nawróceniu poświęcił swoje życie ratowaniu dzieci w Sudanie Południowym i Ugandzie.",
    en: "Sam Childers, known as the 'Machine Gun Preacher', is a former motorcycle gang member who underwent an extraordinary transformation. After his conversion, he dedicated his life to saving children in South Sudan and Uganda.",
    ru: "Сэм Чайлдерс, известный как 'Проповедник с Пулеметом', бывший член мотоциклетной банды, прошедший через необычайное преображение. После обращения он посвятил свою жизнь спасению детей в Южном Судане и Уганде.",
    uk: "Сем Чайлдерс, відомий як 'Проповідник з Кулеметом', колишній член мотоциклетної банди, який пройшов через надзвичайне перетворення. Після навернення він присвятив своє життя порятунку дітей у Південному Судані та Уганді.",
  },
  "about.bio": {
    pl: "Sam Childers, znany jako 'Kaznodzieja z Karabinem', przeszedł niezwykłą przemianę życiową - od członka gangu motocyklowego do założyciela sierocińca w Sudanie Południowym. Od ponad 20 lat ratuje dzieci z terenów objętych wojną, dając im nadzieję na lepszą przyszłość.",
    en: "Sam Childers, known as the 'Machine Gun Preacher', underwent an extraordinary life transformation - from a motorcycle gang member to founding an orphanage in South Sudan. For over 20 years, he has been rescuing children from war-torn areas, giving them hope for a better future.",
    ru: "Сэм Чайлдерс, известный как 'Проповедник с Пулеметом', прошел необычайное жизненное преображение - от члена мотоциклетной банды до основателя приюта в Южном Судане. Более 20 лет он спасает детей из зон военных действий, давая им надежду на лучшее будущее.",
    uk: "Сем Чайлдерс, відомий як 'Проповідник з Кулеметом', пройшов надзвичайне життєве перетворення - від члена мотоциклетної банди до засновника притулку в Південному Судані. Понад 20 років він рятує дітей із зон бойових дій, даючи їм надію на краще майбутнє.",
  },
  "about.history.title": {
    pl: "Historia Sama Childersa",
    en: "Sam Childers' Story",
    ru: "История Сэма Чайлдерса",
    uk: "Історія Сема Чайлдерса",
  },
  "about.history.early": {
    pl: "Sam Childers urodził się w Grand Forks w Północnej Dakocie. Jako nastolatek przyłączył się do gangu motocyklowego, gdzie zajmował się handlem narkotykami i dopuszczał się aktów przemocy. Po nawróceniu i przemianie życiowej w 1992 roku, Sam zdecydował się poświęcić swoje życie Bogu i zaczął pomagać dzieciom w Afryce.",
    en: "Sam Childers was born in Grand Forks, North Dakota. As a teenager, he joined a motorcycle gang and became involved in drugs and violence. After his conversion and life transformation in 1992, Sam decided to dedicate his life to God and began helping children in Africa.",
    ru: "Сэм Чайлдерс родился в Гранд-Форксе, Северная Дакота. В подростковом возрасте он присоединился к мотоциклетной банде и был вовлечен в наркотики и насилие. После обращения и жизненного преображения в 1992 году, Сэм решил посвятить свою жизнь Богу и начал помогать детям в Африке.",
    uk: "Сем Чайлдерс народився в Гранд-Форксі, Північна Дакота. У підлітковому віці він приєднався до мотоциклетної банди і був залучений до наркотиків та насильства. Після навернення та життєвого перетворення в 1992 році, Сем вирішив присвятити своє життя Богу і почав допомагати дітям в Африці.",
  },
  "about.history.turning": {
    pl: "W 1998 roku, podczas swojej pierwszej podróży misyjnej do południowego Sudanu, Sam był świadkiem tragedii, gdy zobaczył ciało dziecka, które zginęło na minie lądowej. To wydarzenie całkowicie zmieniło jego życie i postanowił walczyć o bezpieczeństwo dzieci w regionie.",
    en: "In 1998, during his first missionary trip to South Sudan, Sam witnessed a tragedy when he saw the body of a child killed by a landmine. This event completely changed his life, and he decided to fight for the safety of children in the region.",
    ru: "В 1998 году, во время своей первой миссионерской поездки в Южный Судан, Сэм стал свидетелем трагедии, когда увидел тело ребенка, погибшего от наземной мины. Это событие полностью изменило его жизнь, и он решил бороться за безопасность детей в регионе.",
    uk: "У 1998 році, під час своєї першої місіонерської поїздки до Південного Судану, Сем став свідком трагедії, коли побачив тіло дитини, яка загинула від наземної міни. Ця подія повністю змінила його життя, і він вирішив боротися за безпеку дітей у регіоні.",
  },
  "about.photo.bike": {
    pl: "Sam Childers na swoim motocyklu 'machinegunpreacher'.",
    en: "Sam Childers on his 'machinegunpreacher' motorcycle.",
    ru: "Сэм Чайлдерс на мотоцикле",
    uk: "Сем Чайлдерс на мотоциклі",
  },
  "about.photo.bike.alt": {
    pl: "Sam Childers z motocyklem",
    en: "Sam Childers with his motorcycle",
    ru: "Сэм Чайлдерс на мотоцикле",
    uk: "Сем Чайлдерс на мотоциклі",
  },
  "about.photo.children.alt": {
    pl: "Sam Childers z dziećmi",
    en: "Sam Childers with children",
    ru: "Сэм Чайлдерс с детьми",
    uk: "Сем Чайлдерс з дітьми",
  },
  "about.angels.description": {
    pl: "Angels of East Africa (Anioły Wschodniej Afryki) to organizacja humanitarna założona przez Sama Childersa, która prowadzi sierociniec oraz szkołę dla dzieci ocalałych z wojny domowej w Sudanie Południowym i Ugandzie. Organizacja aktywnie działa na rzecz uratowania dzieci z rąk Lord's Resistance Army (LRA) dowodzonej przez Josepha Kony'ego.",
    en: "Angels of East Africa is a humanitarian organization founded by Sam Childers that runs an orphanage and school for children who survived the civil war in South Sudan and Uganda. The organization actively works to rescue children from the Lord's Resistance Army (LRA) led by Joseph Kony.",
    ru: "Angels of East Africa (Ангелы Восточной Африки) - это гуманитарная организация, основанная Сэмом Чайлдерсом, которая управляет детским домом и школой для детей, переживших гражданскую войну в Южном Судане и Уганде. Организация активно работает над спасением детей из рук Армии сопротивления Господа (LRA) под руководством Джозефа Кони.",
    uk: "Angels of East Africa (Ангели Східної Африки) - це гуманітарна організація, заснована Семом Чайлдерсом, яка керує дитячим будинком та школою для дітей, які пережили громадянську війну в Південному Судані та Уганді. Організація активно працює над порятунком дітей з рук Армії опору Господа (LRA) під керівництвом Джозефа Коні.",
  },
  "about.angels.work": {
    pl: "Organizacja zapewnia schronienie, pożywienie, edukację, opiekę medyczną oraz duchowe wsparcie dla setek dzieci. Samodzielnie budują szkoły, wiercą studnie i zapewniają różne formy pomocy dla lokalnych społeczności.",
    en: "The organization provides shelter, food, education, medical care, and spiritual support to hundreds of children. They independently build schools, drill wells, and provide various forms of assistance to local communities.",
    ru: "Организация обеспечивает убежище, питание, образование, медицинскую помощь и духовную поддержку сотням детей. Они самостоятельно строят школы, бурят скважины и предоставляют различные формы помощи местным общинам.",
    uk: "Організація забезпечує притулок, харчування, освіту, медичну допомогу та духовну підтримку сотням дітей. Вони самостійно будують школи, бурять свердловини та надають різні форми допомоги місцевим громадам.",
  },
  "about.movie.title": {
    pl: 'Film "Machine Gun Preacher"',
    en: 'The "Machine Gun Preacher" Movie',
    ru: 'Фильм "Machine Gun Preacher"',
    uk: 'Фільм "Machine Gun Preacher"',
  },
  "about.movie.description": {
    pl: 'W 2011 roku powstał film "Machine Gun Preacher" (Kaznodzieja z karabinem) oparty na życiu Sama Childersa. W roli głównej wystąpił Gerard Butler. Film opowiada historię przemiany Sama z członka gangu motocyklowego w działacza humanitarnego, który walczy o bezpieczeństwo dzieci w Afryce.',
    en: "In 2011, the film \"Machine Gun Preacher\" was made based on Sam Childers' life, starring Gerard Butler. The film tells the story of Sam's transformation from a motorcycle gang member to a humanitarian activist fighting for children's safety in Africa.",
    ru: 'В 2011 году был снят фильм "Machine Gun Preacher" (Проповедник с пулеметом), основанный на жизни Сэма Чайлдерса. В главной роли снялся Джерард Батлер. Фильм рассказывает историю преображения Сэма из члена мотоциклетной банды в гуманитарного активиста, борющегося за безопасность детей в Африке.',
    uk: 'У 2011 році був знятий фільм "Machine Gun Preacher" (Проповідник з кулеметом), заснований на житті Сема Чайлдерса. У головній ролі знявся Джерард Батлер. Фільм розповідає історію перетворення Сема з члена мотоциклетної банди в гуманітарного активіста, який бореться за безпеку дітей в Африці.',
  },
  "about.movie.details": {
    pl: 'Film był reżyserowany przez Marc Forstera, a oprócz Gerarda Butlera wystąpili w nim także Michelle Monaghan, Michael Shannon i Souleymane Sy Savane. Obraz otrzymał nominację do Złotego Globu za najlepszą piosenkę oryginalną "Machine Gun Preacher" wykonaną przez Chrisa Cornella.',
    en: 'The film was directed by Marc Forster and, alongside Gerard Butler, starred Michelle Monaghan, Michael Shannon, and Souleymane Sy Savane. The film received a Golden Globe nomination for Best Original Song "Machine Gun Preacher" performed by Chris Cornell.',
    ru: 'Фильм был снят режиссером Марком Форстером, и помимо Джерарда Батлера в нем снялись Мишель Монаган, Майкл Шеннон и Сулейман Си Саване. Фильм получил номинацию на Золотой глобус за лучшую оригинальную песню "Machine Gun Preacher" в исполнении Криса Корнелла.',
    uk: 'Фільм був знятий режисером Марком Форстером, і окрім Джерарда Батлера в ньому знялися Мішель Монаган, Майкл Шеннон і Сулейман Сі Саване. Фільм отримав номінацію на Золотий глобус за найкращу оригінальну пісню "Machine Gun Preacher" у виконанні Кріса Корнелла.',
  },
  "about.activities.title": {
    pl: "Działalność",
    en: "Activities",
    ru: "Деятельность",
    uk: "Діяльність",
  },
  "about.activities.intro": {
    pl: "Sam Childers jest obecnie jednym z najbardziej rozpoznawalnych działaczy humanitarnych w Afryce Wschodniej. Jego nieustanna praca i zaangażowanie doprowadziły do utworzenia kompleksowej sieci pomocy, która każdego dnia zmienia życie setek dzieci i całych społeczności. Oto główne obszary jego działalności:",
    en: "Sam Childers is now one of the most recognizable humanitarian activists in East Africa. His continuous work and dedication have led to the creation of a comprehensive aid network that changes the lives of hundreds of children and entire communities every day. Here are the main areas of his activity:",
    ru: "Сэм Чайлдерс сейчас является одним из самых узнаваемых гуманитарных активистов в Восточной Африке. Его непрерывная работа и преданность делу привели к созданию комплексной сети помощи, которая каждый день меняет жизни сотен детей и целых общин. Вот основные направления его деятельности:",
    uk: "Сем Чайлдерс зараз є одним з найбільш впізнаваних гуманітарних активістів у Східній Африці. Його безперервна робота та відданість справі призвели до створення комплексної мережі допомоги, яка щодня змінює життя сотень дітей та цілих громад. Ось основні напрямки його діяльності:",
  },
  "about.activities.orphanages.title": {
    pl: "Sierocińce",
    en: "Orphanages",
    ru: "Детские дома",
    uk: "Дитячі будинки",
  },
  "about.activities.orphanages.description": {
    pl: "Założenie i prowadzenie sierocinców, które dają schronienie setkom dzieci z terenów objętych wojną.",
    en: "Establishing and running orphanages that provide shelter to hundreds of children from war-affected areas.",
    ru: "Создание и управление детскими домами, которые предоставляют убежище сотням детей из районов, затронутых войной.",
    uk: "Створення та керування дитячими будинками, які надають притулок сотням дітей із районів, постраждалих від війни.",
  },
  "about.activities.schools.title": {
    pl: "Szkoły",
    en: "Schools",
    ru: "Школы",
    uk: "Школи",
  },
  "about.activities.schools.description": {
    pl: "Budowa i utrzymanie szkół zapewniających edukację dzieciom, które wcześniej nie miały dostępu do nauki.",
    en: "Building and maintaining schools providing education to children who previously had no access to learning.",
    ru: "Строительство и содержание школ, обеспечивающих образование детям, которые ранее не имели доступа к обучению.",
    uk: "Будівництво та утримання шкіл, що забезпечують освіту дітям, які раніше не мали доступу до навчання.",
  },
  "about.activities.infrastructure.title": {
    pl: "Infrastruktura",
    en: "Infrastructure",
    ru: "Инфраструктура",
    uk: "Інфраструктура",
  },
  "about.activities.infrastructure.description": {
    pl: "Wiercenie studni, budowa klinik medycznych i tworzenie infrastruktury niezbędnej dla lokalnych społeczności.",
    en: "Drilling wells, building medical clinics, and creating infrastructure essential for local communities.",
    ru: "Бурение скважин, строительство медицинских клиник и создание инфраструктуры, необходимой для местных общин.",
    uk: "Буріння свердловин, будівництво медичних клінік та створення інфраструктури, необхідної для місцевих громад.",
  },

  // Events Section
  "events.title": {
    pl: "Wydarzenia",
    en: "Events",
    ru: "События",
    uk: "Події",
  },
  "events.subtitle": {
    pl: "Dołącz do Spotkań z Samem w Polsce",
    en: "Join Sam's Meetings in Poland",
    ru: "Присоединяйтесь к встречам с Сэмом в Польше",
    uk: "Приєднуйтесь до зустрічей із Семом у Польщі",
  },
  "events.description": {
    pl: "Zobacz nadchodzące wydarzenia z udziałem Sama Childersa.",
    en: "See upcoming events featuring Sam Childers.",
    ru: "Смотрите предстоящие события с участием Сэма Чайлдерса.",
    uk: "Дивіться майбутні події за участю Сема Чайлдерса.",
  },
  "events.hall": {
    pl: "Sala",
    en: "Hall",
    ru: "Зал",
    uk: "Зал",
  },
  "events.date": {
    pl: "Data:",
    en: "Date:",
    ru: "Дата:",
    uk: "Дата:",
  },
  "events.time": {
    pl: "Godzina:",
    en: "Time:",
    ru: "Время:",
    uk: "Час:",
  },
  "events.location": {
    pl: "Miejsce:",
    en: "Location:",
    ru: "Место:",
    uk: "Місце:",
  },
  "events.entryFee": {
    pl: "Wstęp",
    en: "Entry Fee",
    ru: "Стоимость входа",
    uk: "Вартість входу",
  },
  "events.agenda.welcome": {
    pl: "Powitanie i wprowadzenie",
    en: "Welcome and Introduction",
    ru: "Приветствие и введение",
    uk: "Привітання та вступ",
  },
  "events.agenda.testimony": {
    pl: "Świadectwo Sama",
    en: "Sam's Testimony",
    ru: "Свидетельство Сэма",
    uk: "Свідчення Сема",
  },
  "events.agenda.qa": {
    pl: "Sesja pytań i odpowiedzi",
    en: "Q&A Session",
    ru: "Сессия вопросов и ответов",
    uk: "Сесія питань та відповідей",
  },
  "events.agenda.prayer": {
    pl: "Modlitwa",
    en: "Prayer",
    ru: "Молитва",
    uk: "Молитва",
  },
  "events.agenda.meeting": {
    pl: "Spotkanie i zdjęcia",
    en: "Meet and Greet",
    ru: "Встреча и фотографии",
    uk: "Зустріч та фотографії",
  },
  "events.buyTicket": {
    pl: "Kup Bilet",
    en: "Buy Ticket",
    ru: "Купить Билет",
    uk: "Купити Квиток",
  },
  "events.schedule.title": {
    pl: "Harmonogram Wydarzeń",
    en: "Event Schedule",
  },
  "events.schedule.description": {
    pl: 'Poniżej znajdziesz szczegółowy harmonogram wydarzeń z udziałem Sama Childersa podczas jego wizyty w Polsce w maju 2025 roku. Wydarzenia oznaczone jako "zamknięte" wymagają specjalnego zaproszenia.',
    en: 'Below you will find a detailed schedule of events with Sam Childers during his visit to Poland in May 2025. Events marked as "closed" require special invitation.',
  },
  "events.closed": {
    pl: "Spotkanie zamknięte",
    en: "Closed meeting",
  },
  "events.open": {
    pl: "Spotkanie otwarte",
    en: "Open meeting",
  },
  "events.details": {
    pl: "Szczegóły wkrótce",
    en: "Details coming soon",
  },
  "events.view.all": {
    pl: "Zobacz pełen harmonogram",
    en: "View full schedule",
  },
  "events.important.title": {
    pl: "Ważne informacje",
    en: "Important Information",
    ru: "Важная информация",
    uk: "Важлива інформація",
  },
  "events.important.schedule": {
    pl: "Harmonogram wydarzeń może ulec zmianie. Prosimy o regularne sprawdzanie aktualizacji.",
    en: "The event schedule may change. Please check regularly for updates.",
    ru: "Расписание мероприятий может измениться. Пожалуйста, регулярно проверяйте обновления.",
    uk: "Розклад заходів може змінитися. Будь ласка, регулярно перевіряйте оновлення.",
  },
  "events.important.closed": {
    pl: "Na wydarzenia zamknięte wstęp możliwy jest wyłącznie z zaproszeniem.",
    en: "Closed events require an invitation for entry.",
    ru: "На закрытые мероприятия вход возможен только по приглашению.",
    uk: "На закриті заходи вхід можливий тільки за запрошенням.",
  },
  "events.important.open": {
    pl: "Na wydarzenia otwarte wstęp jest wolny, ale liczba miejsc może być ograniczona.",
    en: "Open events are free to attend, but seating may be limited.",
    ru: "На открытые мероприятия вход свободный, но количество мест может быть ограничено.",
    uk: "На відкриті заходи вхід вільний, але кількість місць може бути обмежена.",
  },
  "events.important.final": {
    pl: "Na Wielki Finał na Stadionie Miejskim w Poznaniu wymagany jest bilet wstępu w cenie 20zł.",
    en: "The Grand Final at the Municipal Stadium in Poznań requires an entrance ticket priced at 20 PLN.",
    ru: "На Грандиозный Финал на Городском стадионе в Познани требуется входной билет стоимостью 20 злотых.",
    uk: "На Великий Фінал на Міському стадіоні в Познані потрібен вхідний квиток вартістю 20 злотих.",
  },
  "events.important.translation": {
    pl: "Wszystkie wydarzenia będą tłumaczone na język polski i angielski przez tłumacza.",
    en: "All events will be interpreted into Polish and English by an interpreter.",
    ru: "Все мероприятия будут переводиться на польский и английский языки переводчиком.",
    uk: "Всі заходи будуть перекладатися на польську та англійську мови перекладачем.",
  },

  // Final Event
  "final.title": {
    pl: "Wielki Finał",
    en: "Grand Final",
    ru: "Грандиозный Финал",
    uk: "Великий Фінал",
  },
  "final.subtitle": {
    pl: "Stadion Miejski w Poznaniu",
    en: "Poznań Municipal Stadium",
    ru: "Городской стадион в Познани",
    uk: "Міський стадіон у Познані",
  },
  "final.date": {
    pl: "10 maja 2025, 17:00",
    en: "May 10, 2025, 5:00 PM",
    ru: "10 мая 2025, 17:00",
    uk: "10 травня 2025, 17:00",
  },
  "final.description": {
    pl: "Serdecznie zapraszamy na kulminacyjne wydarzenie wizyty Sama Childersa w Polsce – Wielki Finał, który odbędzie się w sobotę, 10 maja 2025 roku, o godzinie 17:00 w sali B17 na Stadionie Miejskim w Poznaniu.",
    en: "We cordially invite you to the culminating event of Sam Childers' visit to Poland - the Grand Final, which will take place on Saturday, May 10, 2025, at 5:00 PM in Hall B17 at the Municipal Stadium in Poznań.",
    ru: "Сердечно приглашаем вас на кульминационное событие визита Сэма Чайлдерса в Польшу – Грандиозный Финал, который состоится в субботу, 10 мая 2025 года, в 17:00 в зале B17 на Городском стадионе в Познани.",
    uk: "Щиро запрошуємо вас на кульмінаційну подію візиту Сема Чайлдерса до Польщі – Великий Фінал, який відбудеться в суботу, 10 травня 2025 року, о 17:00 в залі B17 на Міському стадіоні в Познані.",
  },
  "final.register": {
    pl: "Dołącz do nas",
    en: "Join us",
    ru: "Присоединяйтесь к нам",
    uk: "Приєднуйтесь до нас",
  },
  "final.practical.title": {
    pl: "Informacje Praktyczne",
    en: "Practical Information",
    ru: "Практическая Информация",
    uk: "Практична Інформація",
  },
  "final.practical.entry": {
    pl: "Wstęp wolny, ale prosimy o dołączenie do wydarzenia na facebooku",
    en: "Free entry, but please join the event on Facebook",
    ru: "Вход свободный, но просим присоединиться к событию на Facebook",
    uk: "Вхід вільний, але просимо приєднатися до події на Facebook",
  },
  "final.practical.transport": {
    pl: "Dojazd komunikacją miejską: linie tramwajowe 1, 6, 13, 15 (przystanek Stadion Miejski)",
    en: "Public transport: tram lines 1, 6, 13, 15 (Municipal Stadium stop)",
    ru: "Общественный транспорт: трамвайные линии 1, 6, 13, 15 (остановка Городской стадион)",
    uk: "Громадський транспорт: трамвайні лінії 1, 6, 13, 15 (зупинка Міський стадіон)",
  },
  "final.practical.parking": {
    pl: "Parking dostępny na terenie stadionu (wjazd od ul. Bułgarskiej)",
    en: "Parking available at the stadium (entrance from Bułgarska Street)",
    ru: "Парковка доступна на территории стадиона (въезд с ул. Булгарской)",
    uk: "Паркування доступне на території стадіону (в'їзд з вул. Булгарської)",
  },
  "final.practical.entrance": {
    pl: "Wejście do sali B17 dostępne od godz. 16:30",
    en: "Hall B17 entrance available from 4:30 PM",
    ru: "Вход в зал B17 доступен с 16:30",
    uk: "Вхід до зали B17 доступний з 16:30",
  },
  "final.practical.books": {
    pl: "Na miejscu stoiska z książkami i materiałami informacyjnymi",
    en: "Book stands and information materials available on site",
    ru: "На месте стенды с книгами и информационными материалами",
    uk: "На місці стенди з книгами та інформаційними матеріалами",
  },
  "final.practical.translation": {
    pl: "Wydarzenie będzie prowadzone w języku angielskim z tłumaczeniem na język polski",
    en: "The event will be conducted in English with Polish translation",
    ru: "Мероприятие будет проводиться на английском языке с переводом на польский",
    uk: "Подія буде проводитися англійською мовою з перекладом на польську",
  },
  "final.join.title": {
    pl: "Dołącz do Wydarzenia",
    en: "Join the Event",
    ru: "Присоединиться к Событию",
    uk: "Приєднатися до Події",
  },
  "final.join.description": {
    pl: "Nie przegap tej wyjątkowej okazji, aby usłyszeć inspirującą historię Sama Childersa i dowiedzieć się więcej o jego misji w Afryce.",
    en: "Don't miss this unique opportunity to hear Sam Childers' inspiring story and learn more about his mission in Africa.",
    ru: "Не упустите эту уникальную возможность услышать вдохновляющую историю Сэма Чайлдерса и узнать больше о его миссии в Африке.",
    uk: "Не пропустіть цю унікальну можливість почути надихаючу історію Сема Чайлдерса та дізнатися більше про його місію в Африці.",
  },
  "final.stadium.title": {
    pl: "O Stadionie Miejskim",
    en: "About the Municipal Stadium",
    ru: "О Городском Стадионе",
    uk: "Про Міський Стадіон",
  },
  "final.stadium.description": {
    pl: "Sala B17 znajduje się na pierwszym piętrze stadionu. Wejście od strony ul. Bułgarskiej.",
    en: "Hall B17 is located on the first floor of the stadium. Entrance from Bułgarska Street.",
    ru: "Зал B17 находится на первом этаже стадиона. Вход со стороны ул. Булгарской.",
    uk: "Зала B17 знаходиться на першому поверсі стадіону. Вхід з боку вул. Булгарської.",
  },
  "final.stadium.image.alt": {
    pl: "Stadion Miejski w Poznaniu",
    en: "Municipal Stadium in Poznań",
    ru: "Городской стадион в Познани",
    uk: "Міський стадіон у Познані",
  },
  "stadium.description": {
    pl: "Stadion Miejski w Poznaniu to nowoczesny obiekt sportowy, będący areną meczów klubu Lech Poznań oraz wydarzeń międzynarodowych. Stadion został gruntownie zmodernizowany przed Euro 2012 i obecnie może pomieścić ponad 40 tysięcy widzów.",
    en: "The Municipal Stadium in Poznań is a modern sports facility, serving as the arena for Lech Poznań club matches and international events. The stadium underwent a thorough modernization before Euro 2012 and can now accommodate over 40,000 spectators.",
    ru: "Городской стадион в Познани – это современный спортивный объект, являющийся ареной матчей клуба Лех Познань и международных событий. Стадион был полностью модернизирован перед Евро-2012 и сейчас может вместить более 40 тысяч зрителей.",
    uk: "Міський стадіон у Познані – це сучасний спортивний об'єкт, що є ареною матчів клубу Лех Познань та міжнародних подій. Стадіон був повністю модернізований перед Євро-2012 і зараз може вмістити понад 40 тисяч глядачів.",
  },
  "stadium.room.description": {
    pl: "Sala B17, w której odbędzie się wydarzenie, to nowoczesna przestrzeń konferencyjna oferująca doskonałe warunki akustyczne i komfortowe miejsca siedzące.",
    en: "Hall B17, where the event will take place, is a modern conference space offering excellent acoustic conditions and comfortable seating.",
    ru: "Зал B17, где будет проходить мероприятие, – это современное конференц-пространство, предлагающее отличные акустические условия и комфортные места для сидения.",
    uk: "Зала B17, де відбудеться подія, – це сучасний конференц-простір, що пропонує відмінні акустичні умови та комфортні місця для сидіння.",
  },

  // Press Section
  "press.title": {
    pl: "Prasa",
    en: "Press",
    ru: "Пресса",
    uk: "Преса",
  },
  "press.downloads": {
    pl: "Materiały do Pobrania",
    en: "Downloads",
    ru: "Материалы для скачивания",
    uk: "Матеріали для завантаження",
  },
  "press.articles": {
    pl: "Artykuły Prasowe",
    en: "Press Articles",
    ru: "Пресс-статьи",
    uk: "Прес-статті",
  },
  "press.contact": {
    pl: "Kontakt dla Mediów",
    en: "Media Contact",
    ru: "Контакт для СМИ",
    uk: "Контакт для ЗМІ",
  },
  "press.contact.description": {
    pl: "Jeśli jesteś przedstawicielem mediów i chciałbyś przeprowadzić wywiad z Samem Childersem podczas jego wizyty w Polsce lub potrzebujesz dodatkowych materiałów prasowych, skontaktuj się z:",
    en: "If you are a media representative and would like to interview Sam Childers during his visit to Poland or need additional press materials, please contact:",
    ru: "Если вы представитель СМИ и хотели бы взять интервью у Сэма Чайлдерса во время его визита в Польшу или вам нужны дополнительные пресс-материалы, пожалуйста, свяжитесь с:",
    uk: "Якщо ви представник ЗМІ і хотіли б взяти інтерв'ю у Сема Чайлдерса під час його візиту до Польщі або вам потрібні додаткові прес-матеріали, будь ласка, зв'яжіться з:",
  },
  "press.description": {
    pl: "Zapoznaj się z artykułami prasowymi na temat działalności Sama Childersa.",
    en: "Read press articles about Sam Childers' activities.",
    ru: "Ознакомьтесь со статьями прессы о деятельности Сэма Чайлдерса.",
    uk: "Ознайомтеся зі статтями преси про діяльність Сема Чайлдерса.",
  },
  "press.open": {
    pl: "Otwórz",
    en: "Open",
    ru: "Открыть",
    uk: "Відкрити",
  },

  // FAQ Section
  "faq.title": {
    pl: "Często Zadawane Pytania",
    en: "Frequently Asked Questions",
    ru: "Часто Задаваемые Вопросы",
    uk: "Часті Запитання",
  },
  "faq.description": {
    pl: "Znajdź odpowiedzi na najczęściej zadawane pytania dotyczące wydarzeń z Samem Childersem",
    en: "Find answers to the most frequently asked questions about events with Sam Childers",
    ru: "Найдите ответы на часто задаваемые вопросы о мероприятиях с Сэмом Чайлдерсом",
    uk: "Знайдіть відповіді на найчастіші запитання про події з Семом Чайлдерсом",
  },
  "faq.not.found": {
    pl: "Nie znalazłeś odpowiedzi na swoje pytanie?",
    en: "Haven't found the answer to your question?",
    ru: "Не нашли ответа на свой вопрос?",
    uk: "Не знайшли відповіді на своє запитання?",
  },
  "faq.contact.description": {
    pl: "Skontaktuj się z nami, a postaramy się pomóc.",
    en: "Contact us and we'll try to help.",
    ru: "Свяжитесь с нами, и мы постараемся помочь.",
    uk: "Зв'яжіться з нами, і ми постараємося допомогти.",
  },
  "faq.q1": {
    pl: "Kiedy odbędą się wydarzenia z Samem Childersem?",
    en: "When will the events with Sam Childers take place?",
    ru: "Когда состоятся мероприятия с Сэмом Чайлдерсом?",
    uk: "Коли відбудуться події з Семом Чайлдерсом?",
  },
  "faq.a1": {
    pl: "Wielki Finał odbędzie się 10 maja 2025 roku. Szczegółowy harmonogram dostępny jest w sekcji 'Wydarzenia'.",
    en: "The Grand Final will take place on May 10, 2025. A detailed schedule is available in the 'Events' section.",
    ru: "Грандиозный Финал состоится 10 мая 2025 года. Подробное расписание доступно в разделе 'События'.",
    uk: "Великий Фінал відбудеться 10 травня 2025 року. Детальний розклад доступний у розділі 'Події'.",
  },
  "faq.q2": {
    pl: "Gdzie odbędą się wydarzenia?",
    en: "Where will the events take place?",
    ru: "Где будут проходить мероприятия?",
    uk: "Де відбудуться події?",
  },
  "faq.a2": {
    pl: "Wydarzenia odbędą się w Poznaniu. Wielki Finał odbędzie się w klubie muzycznym B17 na Stadionie Miejskim (INEA Stadion). Szczegółowe adresy i mapy dojazdu znajdują się w sekcji 'Wydarzenia'.",
    en: "The events will take place in Poznań. The Grand Final will be held at the B17 music club at the Municipal Stadium (INEA Stadium). Detailed addresses and directions can be found in the 'Events' section.",
    ru: "Мероприятия пройдут в Познани. Грандиозный Финал состоится в музыкальном клубе B17 на Городском стадионе (INEA Стадион). Подробные адреса и схемы проезда можно найти в разделе 'События'.",
    uk: "Події відбудуться в Познані. Великий Фінал відбудеться в музичному клубі B17 на Міському стадіоні (INEA Стадіон). Детальні адреси та схеми проїзду можна знайти в розділі 'Події'.",
  },
  "faq.q3": {
    pl: "Czy wydarzenia są bezpłatne?",
    en: "Are the events free?",
    ru: "Являются ли мероприятия бесплатными?",
    uk: "Чи є події безкоштовними?",
  },
  "faq.a3": {
    pl: "Większość wydarzeń jest bezpłatna, jednak Wielki Finał wymaga zakupu biletu. Szczegółowe informacje o cenach znajdują się przy każdym wydarzeniu.",
    en: "Most events are free, but the Final Event requires a ticket purchase. Detailed pricing information is available for each event.",
    ru: "Большинство мероприятий бесплатные, но на Финальное Событие требуется покупка билета. Подробная информация о ценах доступна для каждого мероприятия.",
    uk: "Більшість подій безкоштовні, але на Фінальну Подію потрібно придбати квиток. Детальна інформація про ціни доступна для кожної події.",
  },
  "faq.q4": {
    pl: "Czy potrzebuję wcześniejszej rejestracji?",
    en: "Do I need to register in advance?",
    ru: "Нужна ли предварительная регистрация?",
    uk: "Чи потрібна попередня реєстрація?",
  },
  "faq.a4": {
    pl: "Tak, zalecamy wcześniejszą rejestrację na wszystkie wydarzenia, aby zagwarantować sobie miejsce. Rejestracja jest dostępna online poprzez naszą stronę.",
    en: "Yes, we recommend registering in advance for all events to guarantee your spot. Registration is available online through our website.",
    ru: "Да, мы рекомендуем предварительную регистрацию на все мероприятия, чтобы гарантировать себе место. Регистрация доступна онлайн через наш сайт.",
    uk: "Так, ми рекомендуємо попередню реєстрацію на всі події, щоб гарантувати собі місце. Реєстрація доступна онлайн через наш сайт.",
  },
  "faq.q5": {
    pl: "Czy będzie dostępne tłumaczenie?",
    en: "Will translation be available?",
    ru: "Будет ли доступен перевод?",
    uk: "Чи буде доступний переклад?",
  },
  "faq.a5": {
    pl: "Tak, wszystkie wydarzenia będą prowadzone w języku angielskim z tłumaczeniem na język polski oraz w języku polskim z tłumaczeniem na język angielski.",
    en: "Yes, all events will be conducted in English with Polish translation and in Polish with English translation.",
    ru: "Да, все мероприятия будут проводиться на английском языке с переводом на польский и на польском языке с переводом на английский.",
    uk: "Так, усі події будуть проводитися англійською мовою з перекладом на польську та польською мовою з перекладом на англійську.",
  },
  "faq.q6": {
    pl: "Czy mogę przyprowadzić dzieci?",
    en: "Can I bring children?",
    ru: "Можно ли приводить детей?",
    uk: "Чи можна приводити дітей?",
  },
  "faq.a6": {
    pl: "Tak, wydarzenia są odpowiednie dla całych rodzin. Dzieci do 10 roku życia (włącznie) mają wstęp bezpłatny na wszystkie wydarzenia.",
    en: "Yes, the events are suitable for whole families. Children up to and including 10 years of age can attend all events for free.",
    ru: "Да, мероприятия подходят для всей семьи. Дети до 10 лет включительно могут посещать все мероприятия бесплатно.",
    uk: "Так, події підходять для всієї родини. Діти до 10 років включно можуть відвідувати всі події безкоштовно.",
  },
  "faq.q7": {
    pl: "Czy będzie możliwość osobistego spotkania z Samem Childersem?",
    en: "Will there be an opportunity to meet Sam Childers personally?",
    ru: "Будет ли возможность лично встретиться с Сэмом Чайлдерсом?",
    uk: "Чи буде можливість особисто зустрітися з Семом Чайлдерсом?",
  },
  "faq.a7": {
    pl: "Tak, po każdym wydarzeniu będzie możliwość krótkiej rozmowy i zrobienia zdjęcia z Samem Childersem.",
    en: "Yes, after each event there will be an opportunity for a brief conversation and photo with Sam Childers.",
    ru: "Да, после каждого мероприятия будет возможность короткого разговора и фотографии с Сэмом Чайлдерсом.",
    uk: "Так, після кожної події буде можливість короткої розмови та фотографії з Семом Чайлдерсом.",
  },
  "faq.q8": {
    pl: "Czy będzie możliwość wsparcia misji Sama Childersa?",
    en: "Will there be an opportunity to support Sam Childers' mission?",
    ru: "Будет ли возможность поддержать миссию Сэма Чайлдерса?",
    uk: "Чи буде можливість підтримати місію Сема Чайлдерса?",
  },
  "faq.a8": {
    pl: "Tak, podczas wydarzeń będzie możliwość przekazania darowizny na rzecz Angels of East Africa oraz zakupu książek i innych materiałów.",
    en: "Yes, during the events there will be opportunities to make donations to Angels of East Africa and purchase books and other materials.",
    ru: "Да, во время мероприятий будет возможность сделать пожертвование в пользу Angels of East Africa и приобрести книги и другие материалы.",
    uk: "Так, під час подій буде можливість зробити пожертву на користь Angels of East Africa та придбати книги та інші матеріали.",
  },
  "faq.q9": {
    pl: "Czy mogę zarejestrować się na miejscu?",
    en: "Can I register on-site?",
    ru: "Могу ли я зарегистрироваться на месте?",
    uk: "Чи можу я зареєструватися на місці?",
  },
  "faq.a9": {
    pl: "Tak, rejestracja na miejscu będzie możliwa, ale zalecamy wcześniejszą rejestrację online, aby zagwarantować sobie miejsce.",
    en: "Yes, on-site registration will be available, but we recommend registering online in advance to guarantee your spot.",
    ru: "Да, регистрация на месте будет возможна, но мы рекомендуем регистрироваться онлайн заранее, чтобы гарантировать себе место.",
    uk: "Так, реєстрація на місці буде можлива, але ми рекомендуємо реєструватися онлайн заздалегідь, щоб гарантувати собі місце.",
  },
  "faq.q10": {
    pl: "Jaka jest opłata za wstęp na Wielki Finał?",
    en: "How much is the entry fee for the Final Event?",
    ru: "Сколько стоит вход на Финальное Событие?",
    uk: "Скільки коштує вхід на Фінальну Подію?",
  },
  "faq.a10": {
    pl: "Wstęp na Wielki Finał kosztuje 20 zł. Bilety będą dostępne przy wejściu lub można je nabyć wcześniej poprzez rejestrację.",
    en: "The entry fee for the Final Event is 20 PLN. Tickets will be available at the entrance or can be purchased in advance through registration.",
    ru: "Вход на Финальное Событие стоит 20 злотых. Билеты будут доступны при входе или их можно приобрести заранее через регистрацию.",
    uk: "Вхід на Фінальну Подію коштує 20 злотих. Квитки будуть доступні при вході або їх можна придбати заздалегідь через реєстрацію.",
  },
  "faq.q11": {
    pl: "Jak mogę kupić bilety?",
    en: "How can I buy tickets?",
    ru: "Как я могу купить билеты?",
    uk: "Як я можу купити квитки?",
  },
  "faq.a11": {
    pl: "Bilety są obsługiwane przez platformę Evenea.pl. Możesz je kupić online poprzez nasz system rejestracji. Proces jest prosty i bezpieczny. Po zakupie otrzymasz bilet na podany adres email.",
    en: "Tickets are handled through the Evenea.pl platform. You can purchase them online through our registration system. The process is simple and secure. After purchase, you will receive your ticket via email.",
    ru: "Билеты обрабатываются через платформу Evenea.pl. Вы можете приобрести их онлайн через нашу систему регистрации. Процесс простой и безопасный. После покупки вы получите билет на указанный email.",
    uk: "Квитки обробляються через платформу Evenea.pl. Ви можете придбати їх онлайн через нашу систему реєстрації. Процес простий і безпечний. Після покупки ви отримаєте квиток на вказаний email.",
  },
  "faq.q12": {
    pl: "W jakich językach będą prowadzone wydarzenia?",
    en: "In what languages will the events be conducted?",
    ru: "На каких языках будут проводиться мероприятия?",
    uk: "Якими мовами будуть проводитися події?",
  },
  "faq.a12": {
    pl: "Wydarzenia będą prowadzone w języku angielskim z tłumaczeniem na język polski oraz w języku polskim z tłumaczeniem na język angielski. Tłumaczenie będzie wykonywane na żywo ze sceny.",
    en: "Events will be conducted in English with Polish translation and in Polish with English translation. Translation will be provided live from the stage.",
    ru: "Мероприятия будут проводиться на английском языке с переводом на польский и на польском языке с переводом на английский. Перевод будет осуществляться в живую со сцены.",
    uk: "Події будуть проводитися англійською мовою з перекладом на польську та польською мовою з перекладом на англійську. Переклад буде здійснюватися наживо зі сцени.",
  },
  "faq.q13": {
    pl: "Czy jest specjalna cena dla dzieci?",
    en: "Is there a special price for children?",
    ru: "Есть ли специальная цена для детей?",
    uk: "Чи є спеціальна ціна для дітей?",
  },
  "faq.a13": {
    pl: "Tak! Dzieci do 10 roku życia (włącznie) mają wstęp za darmo. Dla dzieci powyżej 10 roku życia obowiązują normalne ceny biletów.",
    en: "Yes! Children up to and including 10 years of age can enter for free. For children over 10 years old, regular ticket prices apply.",
    ru: "Да! Дети до 10 лет включительно могут войти бесплатно. Для детей старше 10 лет действуют обычные цены на билеты.",
    uk: "Так! Діти до 10 років включно можуть увійти безкоштовно. Для дітей старше 10 років діють звичайні ціни на квитки.",
  },

  // Contact Section
  "contact.title": {
    pl: "Skontaktuj się z Nami",
    en: "Contact Us",
    ru: "Свяжитесь с Нами",
    uk: "Зв'яжіться з Нами",
  },
  "contact.subtitle": {
    pl: "Masz pytania? Napisz do nas",
    en: "Have questions? Write to us",
    ru: "Есть вопросы? Напишите нам",
    uk: "Є питання? Напишіть нам",
  },
  "contact.name": {
    pl: "Imię i Nazwisko",
    en: "Full Name",
    ru: "Имя и Фамилия",
    uk: "Ім'я та Прізвище",
  },
  "contact.email": {
    pl: "Email",
    en: "Email",
    ru: "Электронная почта",
    uk: "Електронна пошта",
  },
  "contact.message": {
    pl: "Wiadomość",
    en: "Message",
    ru: "Сообщение",
    uk: "Повідомлення",
  },
  "contact.send": {
    pl: "Wyślij",
    en: "Send",
    ru: "Отправить",
    uk: "Надіслати",
  },
  "contact.coordinator": {
    pl: "Koordynator wydarzenia:",
    en: "Event coordinator:",
    ru: "Координатор мероприятия:",
    uk: "Координатор заходу:",
  },
  "contact.info.title": {
    pl: "Informacje kontaktowe",
    en: "Contact Information",
    ru: "Контактная информация",
    uk: "Контактна інформація",
  },
  "contact.address.title": {
    pl: "Adres korespondencyjny",
    en: "Mailing Address",
    ru: "Почтовый адрес",
    uk: "Поштова адреса",
  },
  "contact.phone.title": {
    pl: "Telefon",
    en: "Phone",
    ru: "Телефон",
    uk: "Телефон",
  },
  "contact.phone.soon": {
    pl: "wkrótce",
    en: "coming soon",
    ru: "скоро будет доступен",
    uk: "скоро буде доступний",
  },
  "contact.aea.title": {
    pl: "Angels of East Africa",
    en: "Angels of East Africa",
    ru: "Angels of East Africa",
    uk: "Angels of East Africa",
  },
  "contact.aea.description": {
    pl: "Aby bezpośrednio wesprzeć organizację Sama Childersa lub dowiedzieć się więcej o działalności Angels of East Africa, odwiedź oficjalną stronę:",
    en: "To directly support Sam Childers' organization or learn more about Angels of East Africa's activities, visit the official website:",
    ru: "Чтобы напрямую поддержать организацию Сэма Чайлдерса или узнать больше о деятельности Angels of East Africa, посетите официальный сайт:",
    uk: "Щоб безпосередньо підтримати організацію Сема Чайлдерса або дізнатися більше про діяльність Angels of East Africa, відвідайте офіційний сайт:",
  },
  "contact.aea.button": {
    pl: "Odwiedź machinegunpreacher.org",
    en: "Visit machinegunpreacher.org",
    ru: "Посетить machinegunpreacher.org",
    uk: "Відвідати machinegunpreacher.org",
  },
  "contact.address.church": {
    pl: "Kościół Chrześcijański ARKA",
    en: "ARKA Christian Church",
    ru: "Христианская Церковь ARKA",
    uk: "Християнська Церква ARKA",
  },
  "contact.address.country": {
    pl: "Polska",
    en: "Poland",
    ru: "Польша",
    uk: "Польща",
  },
  "contact.form.email": {
    pl: "Email",
    en: "Email",
    ru: "Электронная почта",
    uk: "Електронна пошта",
  },
  "contact.form.name": {
    pl: "Imię i Nazwisko",
    en: "Full Name",
    ru: "Имя и Фамилия",
    uk: "Ім'я та Прізвище",
  },

  // Footer
  "footer.rights": {
    pl: "Wszelkie prawa zastrzeżone",
    en: "All rights reserved",
    ru: "Все права защищены",
    uk: "Всі права захищені",
  },
  "footer.quicklinks": {
    pl: "Szybkie linki",
    en: "Quick links",
    ru: "Быстрые ссылки",
    uk: "Швидкі посилання",
  },
  "footer.contact": {
    pl: "Kontakt",
    en: "Contact",
    ru: "Контакты",
    uk: "Контакти",
  },
  "footer.organizations": {
    pl: "Angels of East Africa - organizacja pomagająca dzieciom w Afryce.",
    en: "Angels of East Africa - organization helping children in Africa.",
    ru: "Angels of East Africa - организация, помогающая детям в Африке.",
    uk: "Angels of East Africa - організація, що допомагає дітям в Африці.",
  },
  "footer.church": {
    pl: "Kościół Chrześcijański Arka w Poznaniu - organizacja chrześcijańska",
    en: "Arka Christian Church in Poznań - Christian organization",
    ru: "Христианская Церковь Ковчег в Познани - христианская организация",
    uk: "Християнська Церква Ковчег у Познані - християниська організація",
  },
  "footer.mgp": {
    pl: "Oficjalna Strona MGP",
    en: "Official MGP Website",
    ru: "Официальный Сайт MGP",
    uk: "Офіційний Сайт MGP",
  },

  // Partners Section
  "partners.title": {
    pl: "Partnerzy Wydarzenia",
    en: "Event Partners",
    ru: "Партнеры События",
    uk: "Партнери Події",
  },
  "partners.subtitle": {
    pl: "Poznaj organizacje wspierające wizytę Sama Childersa w Polsce",
    en: "Meet the organizations supporting Sam Childers' visit to Poland",
    ru: "Познакомьтесь с организациями, поддерживающими визит Сэма Чайлдерса в Польшу",
    uk: "Познайомтеся з організаціями, що підтримують візит Сема Чайлдерса до Польщі",
  },
  "partners.roles.main": {
    pl: "Główny organizator",
    en: "Main Organizer",
    ru: "Главный организатор",
    uk: "Головний організатор",
  },
  "partners.roles.foreign": {
    pl: "Partner zagraniczny",
    en: "Foreign Partner",
    ru: "Зарубежный партнер",
    uk: "Закордонний партнер",
  },
  "partners.roles.strategic": {
    pl: "Partner strategiczny",
    en: "Strategic Partner",
    ru: "Стратегический партнер",
    uk: "Стратегічний партнер",
  },
  "partners.roles.local": {
    pl: "Partner lokalny",
    en: "Local Partner",
    ru: "Локальный партнер",
    uk: "Локальний партнер",
  },
  "partners.roles.media": {
    pl: "Partner medialny",
    en: "Media Partner",
    ru: "Медиа-партнер",
    uk: "Медіа-партнер",
  },
  "partners.roles.supporting": {
    pl: "Partner wspierający",
    en: "Supporting Partner",
    ru: "Поддерживающий партнер",
    uk: "Партнер підтримки",
  },
  "partners.descriptions.fpd": {
    pl: "Koordynacja całego wydarzenia i wsparcie logistyczne podczas pobytu Sama Childersa w Polsce.",
    en: "Coordination of the entire event and logistical support during Sam Childers' stay in Poland.",
    ru: "Координация всего мероприятия и логистическая поддержка во время пребывания Сэма Чайлдерса в Польше.",
    uk: "Координація всього заходу та логістична підтримка під час перебування Сема Чайлдерса в Польщі.",
  },
  "partners.descriptions.aea": {
    pl: "Organizacja założona przez Sama Childersa, działająca na rzecz dzieci w Afryce.",
    en: "Organization founded by Sam Childers, working for children in Africa.",
    ru: "Организация, основанная Сэмом Чайлдерсом, работающая для детей в Африке.",
    uk: "Організація, заснована Семом Чайлдерсом, що працює для дітей в Африці.",
  },
  "partners.descriptions.poznan": {
    pl: "Wsparcie organizacyjne i udostępnienie przestrzeni na Stadionie Miejskim.",
    en: "Organizational support and providing space at the Municipal Stadium.",
    ru: "Организационная поддержка и предоставление помещения на Городском стадионе.",
    uk: "Організаційна підтримка та надання приміщення на Міському стадіоні.",
  },
  "partners.descriptions.ku": {
    pl: "Organizacja spotkań i wsparcie wolontariuszy podczas wydarzenia.",
    en: "Organization of meetings and volunteer support during the event.",
    ru: "Организация встреч и поддержка волонтеров во время мероприятия.",
    uk: "Організація зустрічей та підтримка волонтерів під час заходу.",
  },
  "partners.descriptions.ms": {
    pl: "Promocja wydarzenia w mediach i obsługa medialna.",
    en: "Event promotion in media and media coverage.",
    ru: "Продвижение мероприятия в СМИ и медиа-сопровождение.",
    uk: "Просування заходу в ЗМІ та медіа-супровід.",
  },
  "partners.descriptions.fds": {
    pl: "Wsparcie finansowe i promocja wydarzenia wśród swoich członków.",
    en: "Financial support and event promotion among their members.",
    ru: "Финансовая поддержка и продвижение мероприятия среди своих членов.",
    uk: "Фінансова підтримка та просування заходу серед своїх членів.",
  },
  "sponsors.title": {
    pl: "Sponsorzy",
    en: "Sponsors",
    ru: "Спонсоры",
    uk: "Спонсори",
  },
  "sponsors.roles.transport": {
    pl: "Transport",
    en: "Transportation",
    ru: "Транспорт",
    uk: "Транспорт",
  },
  "sponsors.roles.accommodation": {
    pl: "Zakwaterowanie",
    en: "Accommodation",
    ru: "Проживание",
    uk: "Проживання",
  },
  "sponsors.roles.printing": {
    pl: "Materiały drukowane",
    en: "Printed Materials",
    ru: "Печатные материалы",
    uk: "Друковані матеріали",
  },
  "sponsors.roles.catering": {
    pl: "Catering",
    en: "Catering",
    ru: "Кейтеринг",
    uk: "Кейтеринг",
  },
  "sponsors.descriptions.transport": {
    pl: "Zapewnienie transportu dla gości podczas całego wydarzenia.",
    en: "Providing transportation for guests throughout the event.",
    ru: "Обеспечение транспортом гостей на протяжении всего мероприятия.",
    uk: "Забезпечення транспортом гостей протягом усього заходу.",
  },
  "sponsors.descriptions.hotel": {
    pl: "Zakwaterowanie dla Sama Childersa i jego zespołu.",
    en: "Accommodation for Sam Childers and his team.",
    ru: "Проживание для Сэма Чайлдерса и его команды.",
    uk: "Проживання для Сема Чайлдерса та його команди.",
  },
  "sponsors.descriptions.printing": {
    pl: "Druk wszystkich materiałów promocyjnych i informacyjnych.",
    en: "Printing of all promotional and informational materials.",
    ru: "Печать всех рекламных и информационных материалов.",
    uk: "Друк усіх рекламних та інформаційних матеріалів.",
  },
  "sponsors.descriptions.catering": {
    pl: "Catering podczas głównego wydarzenia na Stadionie Miejskim.",
    en: "Catering during the main event at the Municipal Stadium.",
    ru: "Кейтеринг во время главного мероприятия на Городском стадионе.",
    uk: "Кейтеринг під час головного заходу на Міському стадіоні.",
  },
  "partners.become": {
    pl: "Zostań naszym partnerem",
    en: "Become our partner",
    ru: "Станьте нашим партнером",
    uk: "Станьте нашим партнером",
  },
  "partners.become.description": {
    pl: "Jeśli jesteś zainteresowany wsparciem działalności Sama Childersa i chciałbyś zostać partnerem lub sponsorem jego wizyt w Polsce, skontaktuj się z nami.",
    en: "If you are interested in supporting Sam Childers' activities and would like to become a partner or sponsor of his visits to Poland, please contact us.",
    ru: "Если вы заинтересованы в поддержке деятельности Сэма Чайлдерса и хотели бы стать партнером или спонсором его визитов в Польшу, свяжитесь с нами.",
    uk: "Якщо ви зацікавлені в підтримці діяльності Сема Чайлдерса і хотіли б стати партнером або спонсором його візитів до Польщі, зв'яжіться з нами.",
  },
  "partners.list.coming.soon": {
    pl: "Wkrótce udostępnimy pełną listę",
    en: "Full list coming soon",
    ru: "Полный список будет доступен в ближайшее время",
    uk: "Повний список буде доступний найближчим часом",
  },

  // Buy Ticket Page
  "buyTicket.title": {
    pl: "Kup bilet na wydarzenie",
    en: "Buy Event Ticket",
    ru: "Купить билет на событие",
    uk: "Купити квиток на подію",
  },
  "buyTicket.description": {
    pl: "Rejestracja na wydarzenie obsługiwana przez evenea.pl",
    en: "Event registration powered by evenea.pl",
    ru: "Регистрация на событие обслуживается evenea.pl",
    uk: "Реєстрація на подію обслуговується evenea.pl",
  },
  "buyTicket.important": {
    pl: "Ważne Informacje",
    en: "Important Information",
    ru: "Важная Информация",
    uk: "Важлива Інформація",
  },
  "final.hall": "hall",
  "events.agenda": {
    pl: "Program Wydarzenia",
    en: "Event Agenda",
    ru: "Программа Мероприятия",
    uk: "Програма Заходу",
  },
  "events.agenda.opening": {
    pl: "Oficjalne Otwarcie",
    en: "Official Opening",
    ru: "Официальное Открытие",
    uk: "Офіційне Відкриття",
  },
  "events.agenda.worship": {
    pl: "Uwielbienie",
    en: "Worship",
    ru: "Поклонение",
    uk: "Прославлення",
  },
  "events.agenda.announcements": {
    pl: "Ogłoszenia i Przedstawienie Gościa",
    en: "Announcements and Guest Introduction",
    ru: "Объявления и Представление Гостя",
    uk: "Оголошення та Представлення Гостя",
  },
  "events.agenda.announcements.description": {
    pl: "Informacje praktyczne, prezentacja wystawców",
    en: "Practical information, exhibitors presentation",
    ru: "Практическая информация, презентация экспонентов",
    uk: "Практична інформація, презентація експонентів",
  },
  "events.agenda.speech": {
    pl: "Główne Przemówienie Sama Childersa",
    en: "Sam Childers' Main Speech",
    ru: "Главная Речь Сэма Чайлдерса",
    uk: "Головна Промова Сема Чайлдерса",
  },
  "events.agenda.closing": {
    pl: "Modlitwa i Zakończenie",
    en: "Prayer and Closing",
    ru: "Молитва и Закрытие",
    uk: "Молитва та Закриття",
  },
  "events.agenda.note": {
    pl: "Uwaga: Program może ulec zmianie.",
    en: "Note: Agenda may be subject to change.",
    ru: "Примечание: Программа может быть изменена.",
    uk: "Примітка: Програма може бути змінена.",
  },
  "events.date.label": {
    pl: "Data:",
    en: "Date:",
    ru: "Дата:",
    uk: "Дата:",
  },
  "events.time.label": {
    pl: "Godzina:",
    en: "Time:",
    ru: "Время:",
    uk: "Час:",
  },
  "events.location.label": {
    pl: "Miejsce:",
    en: "Location:",
    ru: "Место:",
    uk: "Місце:",
  },
  "events.entry.label": {
    pl: "Wstęp:",
    en: "Entry fee:",
    ru: "Вход:",
    uk: "Вхід:",
  },
  "events.entry.price": {
    pl: "20 PLN - wymagany bilet",
    en: "20 PLN - ticket required",
    ru: "20 PLN - требуется билет",
    uk: "20 PLN - потрібен квиток",
  },
  "events.button.buy_ticket": {
    pl: "Kup Bilet",
    en: "Buy Ticket",
    ru: "Купить Билет",
    uk: "Купити Квиток",
  },
  "events.button.facebook_join": {
    pl: "Dołącz do wydarzenia na Facebooku",
    en: "Join Facebook Event",
    ru: "Присоединиться к событию на Facebook",
    uk: "Приєднатися до події на Facebook",
  },
  "gallery.description": {
    pl: "Zobacz zdjęcia z działalności Sama Childersa w Afryce i na świecie",
    en: "See photos from Sam Childers' activities in Africa and around the world",
    ru: "Смотрите фотографии деятельности Сэма Чайлдерса в Африке и по всему миру",
    uk: "Дивіться фотографії діяльності Сема Чайлдерса в Африці та по всьому світу",
  },
  "gallery.view.more": {
    pl: "Zobacz więcej",
    en: "View more",
    ru: "Смотреть больше",
    uk: "Дивитися більше",
  },
  "gallery.close": {
    pl: "Zamknij galerię",
    en: "Close gallery",
    ru: "Закрыть галерею",
    uk: "Закрити галерею",
  },
  "about.movie.trailer.title": {
    pl: "Machine Gun Preacher - Oficjalny Zwiastun",
    en: "Machine Gun Preacher - Official Trailer",
    ru: "Machine Gun Preacher - Официальный Трейлер",
    uk: "Machine Gun Preacher - Офіційний Трейлер",
  },
};

const LanguageContext = createContext<LanguageContextProps>({
  language: "pl",
  setLanguage: () => {},
  t: () => "",
});

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [language, setLanguage] = useState<Language>("pl");

  const t = (key: string): string => {
    if (!translations[key]) {
      console.warn(`Translation key not found: ${key}`);
      return key;
    }
    return translations[key][language];
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => useContext(LanguageContext);
